import Loading from "@/components/loading";
import { S3ClientContext, DynamoClientContext } from "@/contexts/contexts";
import { ProblemContext, RequestError } from "@/contexts/problemContext";
import { AuthSession, fetchAuthSession, updateUserAttribute } from "@aws-amplify/auth";
import { S3Client } from "@aws-sdk/client-s3";
import { AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogHeader, AlertDialogOverlay, Box, Button, Code, Flex, IconButton, Input, Text, useColorMode, useDisclosure } from "@chakra-ui/react";
import { faCheck, faEdit, faHome, faMoon, faRocket, faSun, faUser, faVideo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { DynamoDBClient } from '@aws-sdk/client-dynamodb'
import { fetchUserAttributes } from 'aws-amplify/auth';
import { apiGetRequest } from "@/functions/requests";
import Sidebar from "@/components/Sidebar";
import TopNav from "@/components/TopNav";

export default function Root() {
	const { appId } = useParams()
	const navigate = useNavigate()
	const { colorMode, toggleColorMode, setColorMode } = useColorMode()
	// Dialog for displaying errors needs state managment
	const { isOpen, onOpen, onClose } = useDisclosure()
	const userNameDisclosure = useDisclosure()
	const cancelRef = useRef(null)
	const cancelUnameRef = useRef(null)

	// State for managing any errors we run into.
	// Provided via context so users can set from anywhere and have univeral clean error handling
	const [problem, setProblem] = useState<RequestError>()
	// State for making direct requests into the edit s3 bucket, under the users prefix only
	const [s3Client, setS3Client] = useState<S3Client>()
	const [dynamoClient, setDynamoClient] = useState<DynamoDBClient>()
	// Username for the user
	const [username, setUsername] = useState<string>()

	async function updateUsername() {
		const { valid } = await apiGetRequest<undefined, { valid: boolean }>(`/username/${username}`)
		if (!valid) {
			console.log("Not valid")
			return
		}
		const output = await updateUserAttribute({
			userAttribute: {
				attributeKey: 'preferred_username',
				value: username!
			}
		})
		userNameDisclosure.onClose()
	}

	// Effect to open a dialouge if we encouter issues ever
	useEffect(() => {
		if (problem) onOpen()
	}, [problem])

	// Use effect for controlling color scheme on the fly
	useEffect(() => {
		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
		const handleChange = (event: MediaQueryListEvent) => {
			setColorMode(event.matches ? 'dark' : 'light');
		};

		mediaQuery.addEventListener('change', handleChange);

		return () => {
			mediaQuery.removeEventListener('change', handleChange);
		};
	}, []);

	// On our first render we need to fetch our access information and then set up our s3 client
	useEffect(() => {
		function createClients(session: AuthSession) {
			if (!session.credentials) return
			setS3Client(new S3Client({
				region: import.meta.env.VITE_AWS_REGION,
				credentials: {
					accessKeyId: session.credentials.accessKeyId,
					secretAccessKey: session.credentials.secretAccessKey,
					sessionToken: session.credentials.sessionToken,
				}
			}))
			setDynamoClient(new DynamoDBClient({
				region: import.meta.env.VITE_AWS_REGION,
				credentials: {
					accessKeyId: session.credentials.accessKeyId,
					secretAccessKey: session.credentials.secretAccessKey,
					sessionToken: session.credentials.sessionToken,
				}
			}))
		}
		fetchAuthSession().then(createClients)
		fetchUserAttributes().then(attributes => {
			if (!attributes.preferred_username) {
				userNameDisclosure.onOpen()
				setUsername(attributes.email)
			} else {
				setUsername(attributes.preferred_username)
			}
		})
	}, [])

	if (!s3Client) return <Loading message="Fetching credentials" fullScreen />
	return (
		<DynamoClientContext.Provider value={dynamoClient}>
			<S3ClientContext.Provider value={s3Client}>
				<ProblemContext.Provider value={[problem, setProblem]}>
					<Flex flexDir='row' w='100vw' maxW='100vw' h='100vh' maxH='100vh' overflow="hidden">
						<Sidebar />
						<Flex flexDir="column" flex={1} overflowY="auto">
							<TopNav username={username} />
							<Box p={6}>
								<Outlet />
							</Box>
						</Flex>
					</Flex>
					<AlertDialog isOpen={userNameDisclosure.isOpen} onClose={userNameDisclosure.onClose} leastDestructiveRef={cancelUnameRef}>
						<AlertDialogOverlay>
							<AlertDialogContent>
								<AlertDialogHeader fontSize='xl' fontWeight='bold'>
									First, we need a username for you
								</AlertDialogHeader>
								<AlertDialogBody gap={3} justifyContent='center' display='flex' flexDir='column'>
									<Input w='100%' value={username} onChange={e => setUsername(e.target.value)} />
									<Button
										leftIcon={<FontAwesomeIcon icon={faCheck} />}
										onClick={updateUsername}
										colorScheme='green'
									>
										Confirm username
									</Button>
								</AlertDialogBody>
							</AlertDialogContent>
						</AlertDialogOverlay>
					</AlertDialog>
					<AlertDialog isOpen={isOpen} onClose={onClose} leastDestructiveRef={cancelRef}>
						<AlertDialogOverlay>
							<AlertDialogContent>
								<AlertDialogHeader fontSize='lg' fontWeight='bold'>
									Problem encountered talking to servers
								</AlertDialogHeader>
								<AlertDialogBody>
									<Flex flexDir='column' gap={3} p={1}>
										<Text fontSize='lg'> A page reload is highly reccomended</Text>
										<Text opacity={.5}>Error {problem?.name} stack:</Text>
										<Code w='100%' maxH={300} overflowY='scroll'>{`${problem?.stack}`}</Code>
										<Flex flexDir='row' w='100%' justifyContent='center' gap={3}>
											<Button flex={1} variant='outline' colorScheme='green' onClick={() => window.location.reload()}>Reload</Button>
											<Button flex={1} variant='outline' colorScheme='red' ref={cancelRef} onClick={onClose}>Close</Button>
										</Flex>
									</Flex>
								</AlertDialogBody>
							</AlertDialogContent>
						</AlertDialogOverlay>
					</AlertDialog>
				</ProblemContext.Provider>
			</S3ClientContext.Provider>
		</DynamoClientContext.Provider >
	)
}
